import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { AuthContext, API } from '../App';
import { FileText, Upload, CheckCircle2, AlertCircle, TrendingUp, User, LogOut, Wand2, Download, Eye, ArrowLeft, Loader2, Lock, MessageSquare } from 'lucide-react';
import Logo from '../components/Logo';
import axios from 'axios';
import { toast } from 'sonner';
import { analytics } from '../lib/analytics';
import { PackageBanner } from '../components/PackageBanner';

export default function CVCheckerPage() {
  const { user, logout, fetchUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [cvInfo, setCvInfo] = useState(null);
  
  // CV Fix states
  const [generatingFix, setGeneratingFix] = useState(false);
  const [fixedCV, setFixedCV] = useState(null);
  const [originalText, setOriginalText] = useState('');
  const [showOriginal, setShowOriginal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [hasPaidAccess, setHasPaidAccess] = useState(false);
  const [canJobSpecific, setCanJobSpecific] = useState(false);

  // Phase 1A: target roles + optional JD
  const [targetRole1, setTargetRole1] = useState('');
  const [targetRole2, setTargetRole2] = useState('');
  const [targetRole3, setTargetRole3] = useState('');
  const [targetCountry, setTargetCountry] = useState('UAE');
  const [targetJD, setTargetJD] = useState('');
  const [showTargetForm, setShowTargetForm] = useState(false);

  const isFreePlan = !user?.subscription_plan || user?.subscription_plan === 'free';

  useEffect(() => {
    fetchCVInfo();
    fetchHistory();
    checkPaidAccess();
    axios.get(`${API}/entitlements/me`)
      .then(r => setCanJobSpecific(!!r.data?.benefit_map?.ats_jd_match_full))
      .catch(() => {});
  }, []);

  const fetchCVInfo = async () => {
    try {
      const response = await axios.get(`${API}/cv/current`);
      setCvInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch CV info', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/cv/history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history', error);
    }
  };

  const checkPaidAccess = async () => {
    try {
      const res = await axios.get(`${API}/billing/entitlements`);
      const ents = res.data?.entitlements || [];
      // Check if user has any paid entitlements (array of entitlement objects)
      const hasCvFix = Array.isArray(ents) && ents.some(e => {
        const entData = e.entitlements || {};
        return entData.cv_fix_downloads > 0 || entData.templates_access || entData.interview_pack_access;
      });
      setHasPaidAccess(!isFreePlan || hasCvFix);
    } catch {
      setHasPaidAccess(!isFreePlan);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.pdf') && !selectedFile.name.endsWith('.doc') && !selectedFile.name.endsWith('.docx')) {
        toast.error('Only PDF, DOC, and DOCX files are supported');
        return;
      }
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum allowed size is 10 MB.', { duration: 5000 });
        e.target.value = '';
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/cv/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message || 'CV uploaded successfully!');
      setFile(null);
      const fileInput = document.getElementById('cv-upload-input');
      if (fileInput) fileInput.value = '';
      await fetchUser();
      await fetchCVInfo();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail, { duration: 5000 });
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail, { duration: 5000 });
      } else {
        toast.error('Upload failed. Please try again.', { duration: 5000 });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/cv/analyze`);
      setResult(response.data);
      setFixedCV(null); // Reset fixed CV when new analysis is done
      toast.success('CV analysis complete!');
      await fetchUser();
      await fetchHistory();
    } catch (error) {
      if (error.response?.status === 429) {
        // Rate limit error (cooldown or daily max)
        toast.error(error.response.data.detail, { duration: 5000 });
      } else if (error.response?.status === 403) {
        // Usage limit reached
        toast.error(error.response.data.detail || "You've reached your limit. Upgrade to Pro to continue.", { duration: 5000 });
      } else if (error.response?.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error(error.response?.data?.detail || 'Analysis failed');
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleGenerateFix = async () => {
    if (!targetRole1.trim()) {
      setShowTargetForm(true);
      toast.error('Please enter your primary target role first');
      return;
    }
    setGeneratingFix(true);
    try {
      const target_roles = [targetRole1, targetRole2, targetRole3].map(r => r.trim()).filter(Boolean);
      const response = await axios.post(`${API}/cv/generate-fix`, {
        target_roles,
        target_country: targetCountry || 'UAE',
        target_jd: targetJD || undefined,
      });
      setFixedCV(response.data.fixed_cv);
      setOriginalText(response.data.original_text || '');
      setShowOriginal(false);
      setShowPreview(true);
      const issues = response.data.validation?.issues || [];
      if (issues.length > 0) {
        toast.warning(`CV optimized — ${issues.length} fact issue(s) auto-corrected.`);
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || "You've reached your limit. Upgrade to Pro to continue.", { duration: 5000 });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate fixed CV');
      }
    } finally {
      setGeneratingFix(false);
    }
  };

  const handleDownloadDOCX = async () => {
    if (!fixedCV?.id) return;
    setDownloadingDocx(true);
    try {
      const response = await axios.get(`${API}/cv/download-docx/${fixedCV.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fixedCV.full_name?.replace(/\s+/g, '_') || 'CV'}_CV.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('DOCX downloaded');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You've reached your limit. Upgrade to Pro to continue.", { duration: 5000 });
      } else {
        toast.error('Failed to download DOCX');
      }
    } finally {
      setDownloadingDocx(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!fixedCV?.id) return;
    
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/cv/download-pdf/${fixedCV.id}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fixedCV.full_name?.replace(/\s+/g, '_') || 'CV'}_Daily_UAE_Jobs_AI.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You've reached your limit. Upgrade to Pro to continue.", { duration: 5000 });
      } else {
        toast.error('Failed to download PDF');
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-[#009639]';
    if (score >= 60) return 'text-yellow-600';
    return 'text-[#CE1126]';
  };

  const isPaidUser = user?.subscription_plan === 'pro' || user?.subscription_plan === 'standard';
  const canCheck = !isFreePlan || (user?.cv_checks_count || 0) < 1;
  const hasCv = cvInfo?.has_cv;
  const canUpdate = cvInfo?.can_update;
  
  // Check if user can download (Pro/Standard OR has CV Fix entitlement OR admin-granted access)
  const canAttemptDownload = isPaidUser || hasPaidAccess || fixedCV?.access_level === 'full_access';

  // Fixed CV Preview Component
  const CVPreview = () => (
    <div className="space-y-6">
      {/* Preview Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => setShowPreview(false)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Analysis
        </Button>
        <div className="flex items-center space-x-3">
          {canAttemptDownload ? (
            <>
              <Button
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="btn-uae-red"
                data-testid="download-pdf-btn"
              >
                {downloading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Downloading...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Download PDF</>
                )}
              </Button>
              <Button
                onClick={handleDownloadDOCX}
                disabled={downloadingDocx}
                variant="outline"
                data-testid="download-docx-btn"
              >
                {downloadingDocx ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" />Download DOCX</>
                )}
              </Button>
            </>
          ) : (
            <Link to="/pricing">
              <Button className="btn-uae-red">
                <Lock className="w-4 h-4 mr-2" />
                Unlock PDF Download — AED 29
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Score Improvement */}
      {(fixedCV?.original_score || fixedCV?.fixed_score) ? (
      <Card className="p-6 bg-gradient-to-r from-[#009639] to-[#00b347] text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium mb-1">ATS Score Improvement</h3>
            <p className="text-sm opacity-90">Your CV has been optimized for ATS systems</p>
          </div>
          <div className="text-right">
            <div className="flex items-center space-x-4">
              <div>
                <p className="text-sm opacity-75">Before</p>
                <p className="text-2xl font-bold">{fixedCV?.original_score || 0}</p>
              </div>
              <div className="text-3xl">→</div>
              <div>
                <p className="text-sm opacity-75">After</p>
                <p className="text-3xl font-bold">{fixedCV?.fixed_score || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
      ) : null}

      {/* Original vs Optimized comparison toggle */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3" data-testid="compare-toggle">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
          <button
            onClick={() => setShowOriginal(false)}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors ${!showOriginal ? 'bg-[#CE1126] text-white' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="view-optimized-btn"
          >Optimized</button>
          <button
            onClick={() => setShowOriginal(true)}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors ${showOriginal ? 'bg-gray-800 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            data-testid="view-original-btn"
          >Original</button>
        </div>
        <p className="text-xs text-gray-500">Compare before you download — we only use facts found in your CV.</p>
      </div>

      {showOriginal ? (
        <Card className="p-8 bg-white shadow-lg" data-testid="original-cv-panel">
          <h2 className="text-lg font-bold text-gray-800 border-b border-gray-200 pb-2 mb-4">YOUR ORIGINAL CV</h2>
          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">{originalText || 'Original CV text is unavailable for this version.'}</pre>
          <div className="mt-5 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
            Missing a company, school/college name, date or certification? We never guess or invent details —
            add it to your CV and re-upload so it appears in the optimized version.
          </div>
        </Card>
      ) : (
      <Card className="p-8 bg-white shadow-lg">
        {/* Header */}
        <div className="text-center border-b pb-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{fixedCV?.full_name}</h2>
          <div className="text-sm text-gray-600 space-x-2">
            {fixedCV?.email && <span>{fixedCV.email}</span>}
            {fixedCV?.phone && <><span>•</span><span>{fixedCV.phone}</span></>}
            {fixedCV?.location && <><span>•</span><span>{fixedCV.location}</span></>}
          </div>
          {fixedCV?.linkedin && (
            <p className="text-sm text-blue-600 mt-1">{fixedCV.linkedin}</p>
          )}
        </div>

        {/* Headline (Phase 1A) */}
        {fixedCV?.headline && (
          <p className="text-center text-gray-700 mb-2 text-sm">{fixedCV.headline}</p>
        )}

        {/* Professional Summary */}
        {fixedCV?.professional_summary && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#CE1126] border-b border-gray-200 pb-2 mb-3">
              PROFESSIONAL SUMMARY
            </h2>
            <p className="text-gray-700 leading-relaxed">{fixedCV.professional_summary}</p>
          </div>
        )}

        {/* Core Competencies (Phase 1A) */}
        {fixedCV?.core_competencies?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#CE1126] border-b border-gray-200 pb-2 mb-3">
              CORE COMPETENCIES
            </h2>
            <div className="flex flex-wrap gap-2">
              {fixedCV.core_competencies.map((c) => (
                <span key={c} className="bg-gray-100 text-gray-800 px-2.5 py-1 rounded text-sm">{c}</span>
              ))}
            </div>
          </div>
        )}

        {/* Experience */}
        {fixedCV?.experience?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#CE1126] border-b border-gray-200 pb-2 mb-3">
              PROFESSIONAL EXPERIENCE
            </h2>
            {fixedCV.experience.map((exp) => (
              <div key={`${exp.employer || exp.company}-${exp.job_title || exp.title}-${exp.start_date}`} className="mb-4">
                <div className="flex justify-between items-start">
                  <h3 className="font-bold text-gray-900">{exp.job_title || exp.title}</h3>
                  <span className="text-sm text-gray-500">
                    {exp.start_date} - {exp.end_date || (exp.is_current ? 'Present' : '')}
                  </span>
                </div>
                <p className="text-[#009639] text-sm mb-2">
                  {exp.employer || exp.company}{exp.location && ` | ${exp.location}`}
                </p>
                <ul className="list-disc list-inside text-gray-700 text-sm space-y-1">
                  {(exp.bullets || exp.achievements || exp.responsibilities || []).map((line, i) => (
                    <li key={`${(exp.employer || exp.company)}-b-${i}`}>{line}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* JD Gap Analysis (Phase 1A) */}
        {fixedCV?.jd_analysis?.missing?.length > 0 && (
          <div className="mb-6" data-testid="jd-gap-panel">
            <h2 className="text-lg font-bold text-amber-700 border-b border-amber-200 pb-2 mb-3">
              MISSING FROM JOB DESCRIPTION
            </h2>
            <p className="text-xs text-gray-500 mb-2">
              These appeared in your target JD but are not verified in your CV. We haven&apos;t added them.
              Consider learning/certifying these if relevant.
            </p>
            <div className="flex flex-wrap gap-2">
              {fixedCV.jd_analysis.missing.map((k) => (
                <span key={k} className="bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-1 rounded text-sm">{k}</span>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {fixedCV?.education?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#CE1126] border-b border-gray-200 pb-2 mb-3">
              EDUCATION
            </h2>
            {fixedCV.education.map((edu, idx) => (
              <div key={idx} className="mb-3">
                <h3 className="font-bold text-gray-900">{edu.degree}</h3>
                <p className="text-[#009639] text-sm">
                  {edu.institution}{edu.location && ` | ${edu.location}`}{edu.year && ` | ${edu.year}`}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Skills */}
        {fixedCV?.skills?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#CE1126] border-b border-gray-200 pb-2 mb-3">
              SKILLS
            </h2>
            <div className="flex flex-wrap gap-2">
              {fixedCV.skills.map((skill, idx) => (
                <span key={idx} className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Certifications */}
        {fixedCV?.certifications?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#CE1126] border-b border-gray-200 pb-2 mb-3">
              CERTIFICATIONS
            </h2>
            <ul className="list-disc list-inside text-gray-700 text-sm">
              {fixedCV.certifications.map((cert, idx) => (
                <li key={idx}>{cert}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Languages */}
        {fixedCV?.languages?.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-bold text-[#CE1126] border-b border-gray-200 pb-2 mb-3">
              LANGUAGES
            </h2>
            <p className="text-gray-700 text-sm">{fixedCV.languages.map((l) => (typeof l === 'string' ? l : `${l.language || ''}${l.proficiency ? ` (${l.proficiency})` : ''}`)).filter(Boolean).join(' • ')}</p>
          </div>
        )}

        {/* Branding Footer */}
        <div className="mt-8 pt-4 border-t text-center">
          <p className="text-xs text-gray-400">
            Optimized by Daily UAE Jobs | dailyuaejobs.com
          </p>
        </div>
      </Card>
      )}

      {/* Upgrade CTA for Free Users */}
      {!hasPaidAccess && (
        <Card className="p-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold mb-2">Download Your Optimized CV</h3>
              <p className="opacity-90">
                Upgrade to a paid plan to download your optimized CV as PDF and DOCX.
              </p>
            </div>
            <Link to="/pricing">
              <Button className="btn-uae-red whitespace-nowrap">
                View Plans
              </Button>
            </Link>
          </div>
        </Card>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Logo linkTo="/dashboard" />
          <nav className="flex items-center space-x-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm">Dashboard</Button>
            </Link>
            <Link to="/account">
              <Button variant="ghost" size="sm">
                <User className="w-4 h-4 mr-2" />
                Account
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <PackageBanner />
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Free ATS CV Checker for UAE Job Seekers</h1>
          <p className="text-gray-600">Upload → Analyze → Fix → Download your optimized CV</p>
        </div>

        {/* Show CV Preview if viewing fixed CV */}
        {showPreview && fixedCV ? (
          hasPaidAccess ? <CVPreview /> : (
            <Card className="p-8 text-center">
              <Lock className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">CV Preview Locked</h3>
              <p className="text-gray-600 mb-4">Upgrade to a paid package to preview and download your optimized CV.</p>
              <a href="/pricing" data-testid="preview-locked-pricing-btn">
                <Button className="btn-uae-red px-6">
                  Explore Plans
                </Button>
              </a>
              <Button variant="outline" className="ml-3" onClick={() => setShowPreview(false)}>
                Back
              </Button>
            </Card>
          )
        ) : (
          <>
            {/* CV Upload/Update Section */}
            {(!hasCv || canUpdate) && !result && (
              <Card className="p-8 mb-8">
                <h3 className="text-xl font-bold mb-4">
                  {hasCv ? 'Update Your CV' : 'Upload Your CV'}
                </h3>
                {cvInfo?.cv_expired && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="cv-expired-note">
                    <p className="text-sm text-amber-800">
                      For your security, your previously uploaded CV was automatically removed after our short
                      retention window. Please upload your CV again to analyze or optimize it.
                    </p>
                  </div>
                )}
                {hasCv && (
                  <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Current CV:</strong> {cvInfo.filename}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      Uploaded: {new Date(cvInfo.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6 hover:border-[#CE1126] transition-colors">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium mb-2">{hasCv ? 'Upload New CV' : 'Upload Your CV'}</p>
                  <p className="text-sm text-gray-600 mb-4">Supports PDF, DOC, and DOCX files</p>
                  
                  <div className="mb-4">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileChange}
                      className="hidden"
                      id="cv-upload-input"
                      data-testid="cv-file-input"
                    />
                    <label htmlFor="cv-upload-input" className="inline-block">
                      <div className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        Choose File
                      </div>
                    </label>
                  </div>
                  
                  {file && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-[#009639] font-medium flex items-center justify-center">
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Selected: {file.name}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        Size: {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  )}
                  
                  {!file && (
                    <p className="text-xs text-gray-500">No file selected</p>
                  )}
                </div>
                
                <Button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="w-full btn-uae-red"
                  data-testid="upload-cv-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    hasCv ? 'Update CV' : 'Upload CV'
                  )}
                </Button>
              </Card>
            )}

            {/* Analyze Section */}
            {hasCv && !result && (
              <Card className="p-8 mb-8">
                {!canCheck ? (
                  <div className="text-center py-8">
                    <AlertCircle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">Free Limit Reached</h3>
                    <p className="text-gray-600 mb-6">
                      You&apos;ve used your 1 free CV check. Upgrade to Pro (AED 99) for premium CV optimization!
                    </p>
                    <Link to="/pricing">
                      <Button className="btn-uae-red" data-testid="upgrade-limit-btn">
                        Upgrade Now
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div>
                    <div className="text-center mb-6">
                      <FileText className="w-16 h-16 text-[#CE1126] mx-auto mb-4" />
                      <h3 className="text-xl font-bold mb-2">Analyze Your CV</h3>
                      <p className="text-gray-600 mb-4">
                        Current CV: <strong>{cvInfo.filename}</strong>
                      </p>
                      <p className="text-sm text-gray-500">
                        Get ATS score, keyword analysis, and improvement suggestions
                      </p>
                    </div>
                    <Button
                      onClick={handleAnalyze}
                      disabled={analyzing}
                      className="w-full btn-uae-red"
                      data-testid="analyze-cv-btn"
                    >
                      {analyzing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyzing...</>
                      ) : (
                        'Analyze CV'
                      )}
                    </Button>
                  </div>
                )}
              </Card>
            )}

            {/* Results Section */}
            {result && (
              <div className="space-y-6">
                {/* Overall Score */}
                <Card className="p-8 text-center score-card">
                  <h2 className="text-2xl font-bold mb-4">Overall ATS Score</h2>
                  <div className={`text-7xl font-bold mb-4 ${getScoreColor(result.overall_score)}`}>
                    {result.overall_score}
                  </div>
                  <p className="text-gray-600 mb-6">out of 100</p>
                  {result.assessment && (
                    <div className="mb-4" data-testid="ats-assessment">
                      <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold ${result.overall_score >= 75 ? 'bg-green-100 text-green-800' : result.overall_score >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                        {result.assessment}
                      </span>
                      {result.disclaimer && <p className="text-xs text-gray-500 mt-3 max-w-md mx-auto">{result.disclaimer}</p>}
                    </div>
                  )}
                  
                  {/* Generate Fixed CV Button */}
                  <div className="flex justify-center space-x-4">
                    <Button onClick={() => setResult(null)} variant="outline" data-testid="check-another-btn">
                      Back to CV Manager
                    </Button>
                    {hasPaidAccess && (
                      <Button
                        onClick={() => setShowTargetForm(true)}
                        disabled={generatingFix}
                        className="btn-uae-green"
                        data-testid="generate-fix-btn"
                      >
                        {generatingFix ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating Fix...</>
                        ) : (
                          <><Wand2 className="w-4 h-4 mr-2" />Optimize My CV</>
                        )}
                      </Button>
                    )}
                  </div>
                </Card>

                {/* Locked premium section — free users only */}
                {result.premium_locked && (
                  <Card className="p-8 border-2 border-dashed border-[#CE1126] bg-red-50/40 text-center" data-testid="premium-locked-section">
                    <div className="w-12 h-12 rounded-full bg-[#CE1126]/10 flex items-center justify-center mx-auto mb-4">
                      <Lock className="w-6 h-6 text-[#CE1126]" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Unlock Your Full ATS Report</h3>
                    <p className="text-sm text-gray-600 max-w-lg mx-auto mb-5">
                      See detailed keyword recommendations, CV improvement insights, and personalized optimization options with our premium career packages.
                    </p>
                    <ul className="text-sm text-gray-700 max-w-md mx-auto text-left space-y-1.5 mb-6">
                      <li className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-gray-400" />Missing UAE keyword list &amp; placement tips</li>
                      <li className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-gray-400" />Section-by-section optimization guidance</li>
                      <li className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-gray-400" />AI-generated CV rewrite &amp; PDF/DOCX download</li>
                    </ul>
                    <a href="/pricing" data-testid="explore-premium-btn">
                      <Button className="btn-uae-red">Explore Premium Plans</Button>
                    </a>
                    <p className="text-xs text-gray-500 mt-3">Upgrade to Pro from AED 99</p>
                  </Card>
                )}
                {showTargetForm && hasPaidAccess && (
                  <Card className="p-6 border-2 border-[#009639]" data-testid="target-role-form">
                    <h3 className="text-lg font-bold mb-1">Target-Role Optimization</h3>
                    <p className="text-sm text-gray-600 mb-4">Tell us the roles you&apos;re targeting so we can optimize your CV for Gulf/UAE recruiters. Up to 3 roles.</p>

                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Primary Target Role *</label>
                        <input type="text" value={targetRole1} onChange={e => setTargetRole1(e.target.value)}
                          placeholder="e.g. Warehouse Supervisor"
                          className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#009639]"
                          data-testid="target-role-1" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Target Country</label>
                        <select value={targetCountry} onChange={e => setTargetCountry(e.target.value)}
                          className="w-full h-9 rounded-md border border-gray-300 px-2 text-sm" data-testid="target-country">
                          <option>UAE</option><option>Saudi Arabia</option><option>Qatar</option>
                          <option>Oman</option><option>Bahrain</option><option>Kuwait</option>
                          <option>GCC</option><option>Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Secondary Role (optional)</label>
                        <input type="text" value={targetRole2} onChange={e => setTargetRole2(e.target.value)}
                          className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#009639]"
                          data-testid="target-role-2" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Third Role (optional)</label>
                        <input type="text" value={targetRole3} onChange={e => setTargetRole3(e.target.value)}
                          className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#009639]"
                          data-testid="target-role-3" />
                      </div>
                    </div>

                    <div className="mb-3">
                      {canJobSpecific ? (
                        <>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Job-Specific Optimization — Paste Target Job Description (optional)
                            <span className="ml-2 text-gray-400 font-normal">We&apos;ll flag missing skills — we won&apos;t invent any.</span>
                          </label>
                          <textarea value={targetJD} onChange={e => setTargetJD(e.target.value)} rows={4}
                            placeholder="Paste the job description / employer requirements here for keyword-matched optimization..."
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#009639]"
                            data-testid="target-jd" />
                        </>
                      ) : (
                        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 flex items-start gap-2" data-testid="jd-locked-note">
                          <Lock className="w-4 h-4 text-gray-400 mt-0.5" />
                          <p className="text-xs text-gray-600">
                            <strong>Job-Specific CV Optimization</strong> (tailor your CV to a pasted job description) is
                            available on <strong>Career Boost</strong> and above. <a href="/pricing" className="text-[#CE1126] underline">Upgrade to unlock</a>.
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-3">
                      <Button onClick={handleGenerateFix} disabled={generatingFix || !targetRole1.trim()}
                        className="btn-uae-green" data-testid="run-optimize-btn">
                        {generatingFix ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Optimizing (Gulf expert)...</>
                                       : <><Wand2 className="w-4 h-4 mr-2" />Optimize My CV Now</>}
                      </Button>
                      <Button variant="outline" onClick={() => setShowTargetForm(false)}>Cancel</Button>
                    </div>
                  </Card>
                )}

                {/* Score Breakdown */}
                {!result.premium_locked && (
                <Card className="p-6">
                  <h3 className="text-xl font-bold mb-6">Score Breakdown</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="font-medium">Structure</span>
                        <span className={`font-bold ${getScoreColor(result.structure_score)}`}>
                          {result.structure_score}%
                        </span>
                      </div>
                      <Progress value={result.structure_score} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="font-medium">Keywords</span>
                        <span className={`font-bold ${getScoreColor(result.keywords_score)}`}>
                          {result.keywords_score}%
                        </span>
                      </div>
                      <Progress value={result.keywords_score} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="font-medium">Clarity</span>
                        <span className={`font-bold ${getScoreColor(result.clarity_score)}`}>
                          {result.clarity_score}%
                        </span>
                      </div>
                      <Progress value={result.clarity_score} className="h-2" />
                    </div>
                    <div>
                      <div className="flex justify-between mb-2">
                        <span className="font-medium">ATS Compatibility</span>
                        <span className={`font-bold ${getScoreColor(result.ats_compatibility_score)}`}>
                          {result.ats_compatibility_score}%
                        </span>
                      </div>
                      <Progress value={result.ats_compatibility_score} className="h-2" />
                    </div>
                  </div>
                </Card>
                )}

                {/* FREE USER: Generic hints + paywall */}
                {result.premium_locked ? null : !hasPaidAccess ? (
                  <>
                    <Card className="p-6">
                      <h3 className="text-xl font-bold mb-4 flex items-center">
                        <TrendingUp className="w-6 h-6 mr-2 text-[#009639]" />
                        Quick Hints
                      </h3>
                      <ul className="space-y-3">
                        {(result.suggestions || []).slice(0, 2).map((s, i) => (
                          <li key={i} className="flex items-start">
                            <CheckCircle2 className="w-5 h-5 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                            <span>{s}</span>
                          </li>
                        ))}
                        <li className="flex items-start text-gray-400">
                          <Lock className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="blur-[3px] select-none">Missing UAE-specific job keywords in experience section</span>
                        </li>
                      </ul>
                    </Card>

                    {/* Locked: Full Suggestions */}
                    <Card className="p-6 border-2 border-dashed border-gray-300 bg-gray-50">
                      <div className="text-center py-3">
                        <Lock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <h3 className="text-base font-bold mb-1">Unlock Full ATS Suggestions</h3>
                        <p className="text-sm text-gray-500">
                          {result.suggestions?.length || 0} detailed improvement suggestions available
                        </p>
                      </div>
                    </Card>

                    {/* Locked: Keywords */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <Card className="p-6 border-2 border-dashed border-gray-300 bg-gray-50 relative overflow-hidden">
                        <h3 className="text-lg font-bold mb-3 text-gray-400">Keywords Found</h3>
                        <div className="flex flex-wrap gap-2 blur-[5px] select-none pointer-events-none">
                          {['project management', 'team leadership', 'UAE market'].map((k, i) => (
                            <span key={i} className="bg-gray-200 text-gray-400 px-3 py-1 rounded-full text-sm">{k}</span>
                          ))}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                          <Lock className="w-6 h-6 text-gray-400" />
                        </div>
                      </Card>
                      <Card className="p-6 border-2 border-dashed border-gray-300 bg-gray-50 relative overflow-hidden">
                        <h3 className="text-lg font-bold mb-3 text-gray-400">Missing UAE Keywords</h3>
                        <div className="flex flex-wrap gap-2 blur-[5px] select-none pointer-events-none">
                          {['visa sponsorship', 'labour card', 'NOC'].map((k, i) => (
                            <span key={i} className="bg-gray-200 text-gray-400 px-3 py-1 rounded-full text-sm">{k}</span>
                          ))}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                          <Lock className="w-6 h-6 text-gray-400" />
                        </div>
                      </Card>
                    </div>

                    {/* Locked: CV Preview */}
                    <Card className="p-6 border-2 border-dashed border-gray-300 bg-gray-50">
                      <div className="text-center py-3">
                        <Lock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <h3 className="text-base font-bold mb-1">Unlock Optimized CV Preview</h3>
                        <p className="text-sm text-gray-500">
                          AI-fixed CV with improved bullet points and ATS-optimized formatting
                        </p>
                      </div>
                    </Card>

                    {/* WhatsApp Payment CTA */}
                    <Card className="p-8 bg-gray-900 text-white" data-testid="whatsapp-payment-cta">
                      <div className="text-center">
                        <h3 className="text-2xl font-bold mb-3">Fix My CV — AED 29</h3>
                        <p className="text-gray-300 mb-6 max-w-md mx-auto">
                          Unlock full ATS suggestions, missing UAE keywords, and download your AI-optimized CV as a professional PDF.
                        </p>
                        <a
                          href="https://wa.me/971521310804?text=Hi%2C%20I%20want%20to%20unlock%20CV%20Fix%20(AED%2029).%20I%20will%20send%20payment%20screenshot%20and%20my%20registered%20email."
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button className="bg-[#25D366] hover:bg-[#1fb855] text-white px-8 py-3 text-base" data-testid="whatsapp-pay-btn">
                            <MessageSquare className="w-5 h-5 mr-2" /> Pay via WhatsApp
                          </Button>
                        </a>
                        <div className="mt-4 text-sm text-gray-400 space-y-1">
                          <p>After payment, send:</p>
                          <p>1. Payment screenshot</p>
                          <p>2. Your registered email</p>
                          <p className="text-gray-500 mt-2">We will activate your account manually.</p>
                        </div>
                      </div>
                    </Card>
                  </>
                ) : (
                  <>
                    {/* PAID USER: Full results */}
                    {/* Suggestions */}
                    <Card className="p-6">
                      <h3 className="text-xl font-bold mb-4 flex items-center">
                        <TrendingUp className="w-6 h-6 mr-2 text-[#009639]" />
                        Improvement Suggestions
                      </h3>
                      <ul className="space-y-3">
                        {result.suggestions.map((suggestion, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircle2 className="w-5 h-5 text-[#009639] mr-2 mt-0.5 flex-shrink-0" />
                            <span>{suggestion}</span>
                          </li>
                        ))}
                      </ul>
                    </Card>

                    {/* Keywords */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <Card className="p-6">
                        <h3 className="text-lg font-bold mb-4 text-[#009639]">Keywords Found</h3>
                        <div className="flex flex-wrap gap-2">
                          {result.keywords_found.slice(0, 15).map((keyword, index) => (
                            <span key={index} className="bg-[#009639] bg-opacity-10 text-[#009639] px-3 py-1 rounded-full text-sm font-medium">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </Card>
                      <Card className="p-6">
                        <h3 className="text-lg font-bold mb-4 text-[#CE1126]">Missing Keywords</h3>
                        <div className="flex flex-wrap gap-2">
                          {result.keywords_missing.slice(0, 15).map((keyword, index) => (
                            <span key={index} className="bg-[#CE1126] bg-opacity-10 text-[#CE1126] px-3 py-1 rounded-full text-sm font-medium">
                              {keyword}
                            </span>
                          ))}
                        </div>
                      </Card>
                    </div>

                    {/* Fix CV CTA */}
                    <Card className="p-6 bg-gradient-to-r from-[#CE1126] to-[#009639] text-white">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-xl font-bold mb-2">Ready to Fix Your CV?</h3>
                          <p className="opacity-90">
                            Let AI improve your CV based on the analysis. Get a professionally formatted, ATS-optimized version.
                          </p>
                        </div>
                        <Button 
                          onClick={handleGenerateFix}
                          disabled={generatingFix}
                          className="bg-white text-gray-900 hover:bg-gray-100 whitespace-nowrap"
                          data-testid="generate-fix-cta-btn"
                        >
                          {generatingFix ? 'Generating...' : 'Generate Fixed CV'}
                        </Button>
                      </div>
                    </Card>
                  </>
                )}
              </div>
            )}

            {/* History */}
            {history.length > 0 && !result && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold mb-4">Your CV Check History</h2>
                <div className="space-y-4">
                  {history.slice(0, 5).map((check) => (
                    <Card key={check.id} className="p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{check.filename}</p>
                          <p className="text-sm text-gray-600">
                            {new Date(check.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className={`text-2xl font-bold ${getScoreColor(check.overall_score)}`}>
                          {check.overall_score}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
